import { useState } from 'react';
import { Button } from './ui/button';
import {
  MessageSquare,
  Phone,
  Mail,
  Book,
  Video,
  Search,
  ExternalLink,
  Send,
  ArrowLeft,
  HelpCircle,
  FileText,
  Zap,
  Cpu
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { BrandMark } from './BrandMark';

interface SupportProps {
  onClose: () => void;
}

const faqData = [
  {
    question: "How do I add a new edge node to my matrix?",
    answer: "Go to Node Config → Provision Node. Enter your hardware's IP address, secure port, and admin hash. The system will automatically detect compatible nodes on your network."
  },
  {
    question: "Why is my optic sensor showing offline or disconnected?",
    answer: "Verify local network power, ensure the assigned IP is static, authenticate your credentials against the central registry, and ensure POE delivery is active. Use the 'Ping Matrix' feature to diagnose routing paths."
  },
  {
    question: "How do I configure cognitive trigger thresholds?",
    answer: "Navigate to Tactical Protocols → Neural Triggers. Adjust sensitivity percentages, define targeted bounding boxes, and set automated playbook actions in the Alert Router section."
  },
  {
    question: "Can I access my neural grid remotely from field devices?",
    answer: "Yes, 3RDI forces zero-trust tunnel connections for all remote nodes. Ensure your device maintains its authorized API key and matches IP whitelists."
  },
  {
    question: "How do I export encrypted tactical logs?",
    answer: "Go to Global Archives, select the event timeframe, and click 'Export Encrypted Bundle'. You can dump the logs into MP4 or proprietary formats for strict chain-of-custody transfer."
  }
];

const contactMethods = [
  {
    icon: Mail,
    title: "Secure Comm (Email)",
    description: "Submit a support packet, answered within 1 hour.",
    action: "support@csense.ai",
    color: "blue"
  },
  {
    icon: MessageSquare,
    title: "Live Command Chat",
    description: "Establish a direct link with an architecture engineer.",
    action: "Establish Link",
    color: "emerald"
  },
  {
    icon: Phone,
    title: "Tactical Voice Link",
    description: "Open an immediate voice line for critical triage.",
    action: "+1-555-CSENSE",
    color: "purple"
  }
];

export function Support({ onClose }: SupportProps) {
  const [activeTab, setActiveTab] = useState<'faq' | 'contact' | 'ticket' | 'docs'>('faq');
  const [ticketData, setTicketData] = useState({
    subject: '',
    category: '',
    priority: 'medium',
    description: ''
  });
  const [searchQuery, setSearchQuery] = useState('');

  const filteredFaq = faqData.filter(item =>
    item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const submitTicket = () => {
    console.log('Submitting support ticket:', ticketData);
    alert('Transmission successful. An operator will respond shortly.');
    setTicketData({ subject: '', category: '', priority: 'medium', description: '' });
  };

  const TabButton = ({ value, icon: Icon, label }: { value: any, icon: any, label: string }) => {
    const isActive = activeTab === value;
    return (
      <button
        onClick={() => setActiveTab(value)}
        className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-sm font-bold uppercase tracking-widest transition-all ${
           isActive 
             ? 'bg-blue-600 shadow-[0_0_15px_rgba(37,99,235,0.4)] text-white' 
             : 'text-blue-100/40 hover:text-white hover:bg-white/5'
        }`}
      >
        <Icon className="w-4 h-4" />
        <span className="hidden sm:inline">{label}</span>
      </button>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-[#0a0b14] text-white overflow-hidden">
      {/* Ambient Neural Background */}
      <div className="absolute inset-0 pointer-events-none z-0">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-blue-600/10 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-purple-600/10 blur-[120px]" />
        <div className="absolute top-[20%] right-[-5%] w-[30%] h-[30%] rounded-full bg-emerald-600/5 blur-[100px]" />
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03] mix-blend-overlay" />
      </div>

      {/* Premium Header */}
      <div className="p-4 md:p-6 flex-shrink-0 bg-[#0a0b14]/80 border-b border-white/10 backdrop-blur-2xl relative z-10">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <div className="flex items-center gap-4">
                  <BrandMark className="h-14 w-14 drop-shadow-[0_0_16px_rgba(59,130,246,0.45)]" />
            <div>
                     <h2 className="text-xl md:text-2xl font-black uppercase tracking-tighter shadow-sm">3RDI Support</h2>
                     <p className="text-[10px] text-blue-400 font-bold uppercase tracking-widest">Enterprise Assistance Center</p>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-blue-100/40 hover:text-white hover:bg-white/10 transition-all rounded-xl border border-transparent hover:border-white/10"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Return to Matrix
          </Button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto relative z-10 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          
          {/* Custom Tabs Navigation */}
          <div className="flex p-1.5 bg-black/40 border border-white/10 backdrop-blur-xl rounded-2xl mb-8 overflow-x-auto gap-1">
             <TabButton value="faq" icon={HelpCircle} label="Archives" />
             <TabButton value="contact" icon={MessageSquare} label="Comm Link" />
             <TabButton value="ticket" icon={FileText} label="Ticket Transmission" />
             <TabButton value="docs" icon={Book} label="Neural Docs" />
          </div>

          <AnimatePresence mode="wait">
             <motion.div
               key={activeTab}
               initial={{ opacity: 0, y: 10 }}
               animate={{ opacity: 1, y: 0 }}
               exit={{ opacity: 0, y: -10 }}
               transition={{ duration: 0.2 }}
             >
                {/* FAQ TAB */}
                {activeTab === 'faq' && (
                   <div className="space-y-6">
                      <div className="relative group">
                         <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-blue-500 group-focus-within:text-blue-400 transition-colors" />
                         <input
                           type="text"
                           placeholder="Search knowledge archives..."
                           value={searchQuery}
                           onChange={(e) => setSearchQuery(e.target.value)}
                           className="w-full bg-black/40 border border-white/10 py-5 pl-14 pr-4 rounded-2xl text-white placeholder-blue-100/30 font-light focus:outline-none focus:border-blue-500/50 shadow-inner transition-colors"
                         />
                      </div>
                      <div className="space-y-4">
                         {filteredFaq.map((item, idx) => (
                            <div key={idx} className="bg-white/5 border border-white/10 backdrop-blur-xl p-6 rounded-2xl hover:bg-white/10 hover:border-blue-500/30 transition-all">
                               <h3 className="text-lg font-black text-white mb-3 tracking-tight">{item.question}</h3>
                               <p className="text-sm font-light leading-relaxed text-blue-100/60">{item.answer}</p>
                            </div>
                         ))}
                      </div>
                   </div>
                )}

                {/* CONTACT TAB */}
                {activeTab === 'contact' && (
                   <div className="space-y-8">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                         {contactMethods.map((method, idx) => (
                            <div key={idx} className={`bg-white/5 border border-white/10 backdrop-blur-xl p-8 rounded-3xl text-center hover:bg-white/10 hover:-translate-y-1 transition-all group overflow-hidden relative
                               ${method.color === 'blue' ? 'hover:border-blue-500/40' : method.color === 'emerald' ? 'hover:border-emerald-500/40' : 'hover:border-purple-500/40'}
                            `}>
                               <div className={`absolute -top-10 -right-10 w-32 h-32 bg-${method.color}-500/10 rounded-full blur-[30px] opacity-0 group-hover:opacity-100 transition-opacity`} />
                               <div className={`w-14 h-14 mx-auto rounded-2xl bg-${method.color}-500/10 border border-${method.color}-500/20 flex items-center justify-center mb-6`}>
                                   <method.icon className={`h-6 w-6 text-${method.color}-400 drop-shadow-[0_0_10px_currentColor]`} />
                               </div>
                               <h3 className="text-lg font-bold text-white mb-2">{method.title}</h3>
                               <p className="text-xs text-blue-100/60 font-light mb-8 h-8">{method.description}</p>
                               <Button className={`w-full font-black text-[10px] uppercase tracking-widest bg-${method.color}-600 hover:bg-${method.color}-500 shadow-[0_0_15px_rgba(0,0,0,0.3)] transition-all`}>
                                  {method.action}
                               </Button>
                            </div>
                         ))}
                      </div>

                      <div className="bg-gradient-to-r from-blue-900/10 to-purple-900/10 border border-blue-500/20 backdrop-blur-xl rounded-3xl p-8 text-center relative overflow-hidden">
                         <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.05] mix-blend-overlay pointer-events-none" />
                         <h3 className="text-2xl font-black uppercase tracking-tighter text-white mb-2">Tactical Command Hours</h3>
                         <p className="text-[10px] font-bold text-blue-400 uppercase tracking-widest mb-8">System Availability SLA</p>
                         
                         <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto">
                            <div className="bg-black/40 border border-white/10 rounded-2xl p-6 relative overflow-hidden">
                               <div className="absolute top-0 left-0 w-1 h-full bg-blue-500" />
                               <h4 className="flex items-center text-sm font-bold uppercase tracking-widest text-white mb-4"><Zap className="w-4 h-4 text-blue-400 mr-2"/> Default Clearance</h4>
                               <ul className="space-y-2 text-xs font-mono text-blue-100/60 text-left pl-2">
                                  <li>MON-FRI: <span className="text-white">0900 - 1800 EST</span></li>
                                  <li>SAT: <span className="text-white">1000 - 1600 EST</span></li>
                                  <li>SUN: <span className="text-blue-500">STANDBY MODE</span></li>
                               </ul>
                            </div>
                            <div className="bg-red-950/20 border border-red-500/30 rounded-2xl p-6 relative overflow-hidden">
                               <div className="absolute top-0 left-0 w-1 h-full bg-red-500 shadow-[0_0_10px_#ef4444]" />
                               <h4 className="flex items-center text-sm font-bold uppercase tracking-widest text-white mb-2"><Cpu className="w-4 h-4 text-red-500 mr-2"/> Apex Support</h4>
                               <p className="text-xs text-red-200/60 font-light mb-4 text-left pl-2">Critical infrastructure node failure triaged instantly under SLA protocol.</p>
                               <div className="bg-red-500/20 text-red-400 font-mono text-xs py-2 px-3 rounded text-center border border-red-500/20 shadow-inner">
                                  HOTLINE: +1-800-CS-APEX
                               </div>
                            </div>
                         </div>
                      </div>
                   </div>
                )}

                {/* TICKET TAB */}
                {activeTab === 'ticket' && (
                   <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-3xl p-8 relative overflow-hidden">
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-[1px] bg-gradient-to-r from-transparent via-blue-500/50 to-transparent" />
                      <h2 className="text-2xl font-black uppercase tracking-tight flex items-center gap-3 mb-8">
                         <FileText className="w-6 h-6 text-blue-400 drop-shadow-[0_0_5px_rgba(96,165,250,0.8)]" /> Transmit Code Ticket
                      </h2>
                      
                      <div className="space-y-6">
                         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                               <label className="text-[10px] font-black uppercase tracking-widest text-blue-100/60">Reference Designation *</label>
                               <input
                                  type="text"
                                  placeholder="Brief summary of anomalous logic"
                                  value={ticketData.subject}
                                  onChange={(e) => setTicketData({ ...ticketData, subject: e.target.value })}
                                  className="w-full bg-black/40 border border-white/10 text-white placeholder:text-white/20 py-4 px-4 rounded-2xl focus:outline-none focus:border-blue-500/50 transition-colors"
                               />
                            </div>
                            <div className="space-y-2">
                               <label className="text-[10px] font-black uppercase tracking-widest text-blue-100/60">System Area</label>
                               <input
                                  type="text"
                                  placeholder="e.g. Node Sync, Auth Matrix"
                                  value={ticketData.category}
                                  onChange={(e) => setTicketData({ ...ticketData, category: e.target.value })}
                                  className="w-full bg-black/40 border border-white/10 text-white placeholder:text-white/20 py-4 px-4 rounded-2xl focus:outline-none focus:border-blue-500/50 transition-colors"
                               />
                            </div>
                         </div>

                         <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-blue-100/60">Severity Level</label>
                            <div className="flex flex-wrap gap-2 p-1.5 bg-black/40 border border-white/10 rounded-2xl inline-flex w-full md:w-auto">
                               {['low', 'medium', 'high', 'critical'].map((p) => (
                                  <button
                                     key={p}
                                     onClick={() => setTicketData({ ...ticketData, priority: p })}
                                     className={`flex-1 md:flex-none px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                                        ticketData.priority === p
                                           ? p === 'critical' ? 'bg-red-500 text-white shadow-[0_0_15px_rgba(239,68,68,0.5)]' : 'bg-blue-600 text-white shadow-[0_0_15px_rgba(37,99,235,0.4)]'
                                           : 'text-blue-100/40 hover:text-white hover:bg-white/5'
                                     }`}
                                  >
                                     {p}
                                  </button>
                               ))}
                            </div>
                         </div>

                         <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-blue-100/60">Log Details *</label>
                            <textarea
                               placeholder="Inject full stack traces, reproduction steps, or error hex codes..."
                               value={ticketData.description}
                               onChange={(e) => setTicketData({ ...ticketData, description: e.target.value })}
                               rows={6}
                               className="w-full bg-black/40 border border-white/10 text-white placeholder:text-white/20 py-4 px-4 rounded-2xl focus:outline-none focus:border-blue-500/50 transition-colors resize-none font-mono text-sm"
                            />
                         </div>

                         <div className="flex justify-end pt-4 border-t border-white/10">
                            <Button
                               onClick={submitTicket}
                               disabled={!ticketData.subject || !ticketData.description}
                               className="bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50 font-black text-[10px] uppercase tracking-widest py-6 px-8 rounded-2xl shadow-[0_0_20px_rgba(37,99,235,0.4)] transition-all"
                            >
                               <Send className="w-4 h-4 mr-3" /> Execute Send
                            </Button>
                         </div>
                      </div>
                   </div>
                )}

                {/* DOCS TAB */}
                {activeTab === 'docs' && (
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {[
                         { icon: Book, title: "Tactical Manual", desc: "Complete guide for field deployment.", color: "blue" },
                         { icon: Video, title: "Simulation Loops", desc: "Video-based scenario walk-throughs.", color: "emerald" },
                         { icon: FileText, title: "API Reference", desc: "Neural hooks for developers.", color: "purple" },
                         { icon: HelpCircle, title: "Anomaly Resolution", desc: "Troubleshooting common matrix bugs.", color: "orange" }
                      ].map((doc, idx) => (
                         <div key={idx} className={`bg-white/5 border border-white/10 backdrop-blur-xl p-6 rounded-3xl hover:bg-white/10 transition-all cursor-pointer group flex flex-col h-full 
                            ${doc.color === 'blue' ? 'hover:border-blue-500/30' : doc.color === 'emerald' ? 'hover:border-emerald-500/30' : doc.color === 'purple' ? 'hover:border-purple-500/30' : 'hover:border-orange-500/30'}
                         `}>
                            <div className="flex items-center gap-4 mb-4">
                               <div className={`p-3 rounded-xl bg-${doc.color}-500/10 border border-${doc.color}-500/20`}>
                                  <doc.icon className={`h-5 w-5 text-${doc.color}-400 group-hover:drop-shadow-[0_0_8px_currentColor] transition-all`} />
                               </div>
                               <h3 className="text-white font-black uppercase tracking-tight">{doc.title}</h3>
                            </div>
                            <p className="text-blue-100/60 text-xs font-light mb-6 flex-1">{doc.desc}</p>
                            <Button
                               variant="outline"
                               className={`w-full border-white/10 bg-black/40 text-${doc.color}-300 hover:bg-white/10 hover:text-white transition-all font-bold text-[10px] uppercase tracking-widest py-5 rounded-2xl`}
                            >
                               <ExternalLink className="h-3 w-3 mr-2" /> Open File
                            </Button>
                         </div>
                      ))}
                   </div>
                )}
             </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}